/* Pantheratec Solarrechner v1.1 — no libraries, no API keys, no cookies. */
(function () {
  'use strict';

  const DEFAULT_DEVICES = [
    {id:'fridge',name:'Kompressor-Kühlschrank',watts:45,hours:24,duty:35,qty:1,source:'dc',enabled:true},
    {id:'led',name:'LED-Beleuchtung',watts:14,hours:4,duty:100,qty:1,source:'dc',enabled:true},
    {id:'laptop',name:'Laptop / Notebook',watts:60,hours:2,duty:100,qty:1,source:'ac',enabled:true},
    {id:'phone',name:'Handy / USB-Laden',watts:15,hours:3,duty:100,qty:2,source:'dc',enabled:true},
    {id:'pump',name:'Wasserpumpe',watts:60,hours:0.3,duty:100,qty:1,source:'dc',enabled:true},
    {id:'heater',name:'Dieselheizung (Elektrik)',watts:25,hours:8,duty:35,qty:1,source:'dc',enabled:false},
    {id:'fan',name:'Dachlüfter',watts:20,hours:8,duty:100,qty:1,source:'dc',enabled:false},
    {id:'tv',name:'TV / Monitor',watts:45,hours:3,duty:100,qty:1,source:'ac',enabled:false},
    {id:'coffee',name:'Kaffeemaschine',watts:800,hours:0.2,duty:100,qty:1,source:'ac',enabled:false}
  ];
  const SUN_PRESETS = {
    mitteleuropa:{sommer:4,uebergang:2.4,winter:0.9},
    suedeuropa:{sommer:5,uebergang:3.3,winter:1.9},
    nordeuropa:{sommer:3.4,uebergang:1.8,winter:0.5}
  };
  const RENDER_NAMES = {solar:'Solarmodule',controller:'MPPT-Laderegler',battery:'Bordbatterien',accessory:'Montage & Zubehör'};
  const QUERY_TERMS = {
    solar:['Solarmodul','Solarpanel'],
    controller:['MPPT Solarregler','Solar Laderegler'],
    battery:['LiFePO4 Batterie','AGM Batterie'],
    accessory:['Solarkabel','Solar Dachdurchführung']
  };
  const clamp = (n,min,max) => Math.min(max,Math.max(min,n));
  const isNumber = n => typeof n === 'number' && Number.isFinite(n);
  const numeric = (value,fallback=NaN) => {
    if(value === null || value === undefined || value === '')return fallback;
    const n=Number(String(value).replace(',','.'));
    return Number.isFinite(n)?n:fallback;
  };
  const ceilStep = (n,step) => n<=0?0:Math.ceil((n-0.00000001)/step)*step;
  const fmt = (n,digits=0) => new Intl.NumberFormat('de-DE',{maximumFractionDigits:digits}).format(n);
  const copyDefaultDevices = () => DEFAULT_DEVICES.map(x=>({...x}));

  /** Pure model, can be tested independently of Shopify/DOM. */
  function compute(input){
    const voltage = numeric(input.voltage,12);
    const systemVoltage = voltage===24?24:12;
    const batteryType = ['agm','wet'].includes(input.batteryType)?input.batteryType:'lifepo4';
    // Planning cutoffs, not battery BMS or manufacturer-configured shutdowns.
    const minSoc = batteryType==='lifepo4' ? 0.20 : 0.50;
    const depth = 1-minSoc;
    const startingSoc = clamp(numeric(input.startingSoc,100)/100,0,1);
    const nomBatteryV = systemVoltage===24 ? (batteryType==='lifepo4'?25.6:24) : (batteryType==='lifepo4'?12.8:12);
    const inverterEfficiency=clamp(numeric(input.inverterEfficiency,.9),.75,1);
    const sun=clamp(numeric(input.sunHours,4),.5,8);
    const shade=clamp(numeric(input.shade,.85),.4,1);
    const reserve=clamp(numeric(input.reserve,.2),0,.6);
    const autonomy=clamp(numeric(input.autonomy,1),1,7);
    const existingAh=clamp(numeric(input.existingAh,0),0,5000);
    const devices=(Array.isArray(input.devices)?input.devices:[]).filter(d=>d.enabled).map(d=>{
      const watts=clamp(numeric(d.watts,0),0,5000);
      const hours=clamp(numeric(d.hours,0),0,24);
      const duty=clamp(numeric(d.duty,0),0,100);
      const qty=clamp(numeric(d.qty,1),1,20);
      const wh=watts*hours*(duty/100)*qty;
      return {name:String(d.name||'Gerät'),wh,source:d.source==='ac'?'ac':'dc'};
    });
    const dcWh = devices.reduce((s,d)=>s+(d.source==='dc'?d.wh:0),0);
    const peakSingleACWatts = Math.max(0,...(Array.isArray(input.devices)?input.devices:[]).filter(d=>d.enabled&&d.source==='ac').map(d=>clamp(numeric(d.watts,0),0,5000)*clamp(numeric(d.qty,1),1,20)));
    const acWh = devices.reduce((s,d)=>s+(d.source==='ac'?d.wh:0),0);
    // Battery demand includes 5% distribution and DC conversion overhead.
    // AC energy additionally includes the inverter's own conversion losses.
    const demandWh = (dcWh + acWh / inverterEfficiency) * 1.05;
    const systemFactor=.75; // planning assumption, not a measured guarantee
    const solarWp=ceilStep(demandWh*(1+reserve)/(sun*systemFactor*shade),10);
    const dayGenerationWh=solarWp*sun*systemFactor*shade;
    const usableFactor=depth*.92; // depth of discharge x discharge efficiency
    const neededAh=ceilStep(demandWh*autonomy/(nomBatteryV*usableFactor),10);
    // Available energy depends on the present SOC and stops at the protected floor.
    const currentBatteryWh=existingAh*nomBatteryV*Math.max(0,startingSoc-minSoc)*.92;
    const daysAutonomy=demandWh>0?currentBatteryWh/demandWh:0;
    const plannedBatteryWh=neededAh*nomBatteryV*usableFactor; // new battery starts at 100 % SOC
    const projectedEnergyWh=existingAh>0?currentBatteryWh:plannedBatteryWh;
    const forecastBasis=existingAh>0?'existing':'recommended';
    const forecastNoSunDays=autonomyDays(projectedEnergyWh,demandWh,0);
    const forecastWeakSunDays=autonomyDays(projectedEnergyWh,demandWh,dayGenerationWh*.30);
    const forecastNormalSunDays=autonomyDays(projectedEnergyWh,demandWh,dayGenerationWh);
    const deficitAh=Math.max(0,neededAh-existingAh);
    const chargingV=systemVoltage===24?28.8:14.4;
    const minControllerA=ceilStep((solarWp/chargingV)*1.25,5);
    const roofLength=numeric(input.roofLength,0);
    const roofWidth=numeric(input.roofWidth,0);
    const roofM2=roofLength>0&&roofWidth>0?(roofLength*roofWidth/1000000):0;
    const roofWpApprox=roofM2>0?Math.floor(roofM2*180/10)*10:0;
    return {
      voltage:systemVoltage,batteryType,nomBatteryV,sun,shade,reserve,autonomy,existingAh,
      dcWh,acWh,peakSingleACWatts,devices,demandWh,systemFactor,solarWp,dayGenerationWh,
      depth,minSoc,startingSoc,usableFactor,neededAh,currentBatteryWh,daysAutonomy,deficitAh,
      plannedBatteryWh,forecastBasis,forecastNoSunDays,forecastWeakSunDays,forecastNormalSunDays,
      minControllerA,roofM2,roofWpApprox,
      inverterEfficiency
    };
  }
  /** Days until the chosen minimum SOC at a constant daily PV yield.
   * null means a non-negative modeled energy balance; NOT guaranteed autonomy.
   */
  function autonomyDays(batteryWh, demandWh, pvWh=0){
    if(!(batteryWh>0) || !(demandWh>0))return 0;
    const deficit=demandWh-Math.max(0,pvWh);
    if(deficit<=0)return null;
    return batteryWh/deficit;
  }
  function formatDays(days){
    if(days===null)return 'Tagesbilanz ausgeglichen*';
    if(!Number.isFinite(days)||days<=0)return '0 Std.';
    if(days<1)return fmt(Math.floor(days*24*10)/10,1)+' Std.';
    const safeDays=Math.floor(days*10)/10;
    return fmt(safeDays,1)+(safeDays===1?' Tag':' Tage');
  }
  function batteryStoredWh(ah,result,soc=1){
    return Math.max(0,ah)*result.nomBatteryV*Math.max(0,soc-result.minSoc)*.92;
  }
  function classifyVerified(item,result){
    if(!item||!item.available)return null;
    const type=item.type;
    if(type==='solar'){
      if(!(item.watts>0)||!(result.solarWp>0))return null;
      const qty=Math.ceil(result.solarWp/item.watts);
      if(qty<1||qty>6)return null;
      if(isNumber(item.stock) && item.stock<qty)return null;
      if(result.roofM2>0 && item.areaM2>0 && (qty*item.areaM2)>(result.roofM2*1.01))return null;
      return {item,qty,totalWp:qty*item.watts,extra:qty*item.watts-result.solarWp,sub:`${qty} × ${fmt(item.watts)} Wp = ${fmt(qty*item.watts)} Wp · benötigte Leistung abgedeckt`};
    }
    if(type==='controller'){
      const pvLimit=result.voltage===24?item.pv24:item.pv12;
      if(!(item.amps>=result.minControllerA) || !(pvLimit>=result.solarWp))return null;
      return {item,qty:1,extra:item.amps-result.minControllerA,sub:`${fmt(item.amps)} A Ladestrom · max. ${fmt(pvLimit)} Wp bei ${result.voltage} V`};
    }
    if(type==='battery'){
      if(result.neededAh<=result.existingAh)return null;
      if(item.systemVoltage!==result.voltage || item.batteryKind!==result.batteryType || !(item.batteryAh>=result.neededAh))return null;
      return {item,qty:1,extra:item.batteryAh-result.neededAh,sub:`${fmt(item.batteryAh)} Ah / ${result.voltage} V · als eigenständiger neuer Speicher`};
    }
    if(type==='accessory')return {item,qty:1,extra:0,sub:item.note||'Bitte Ausführung und elektrische Eignung prüfen'};
    return null;
  }
  function rankVerified(products,result,type){
    return (Array.isArray(products)?products:[]).filter(p=>p.type===type).map(p=>classifyVerified(p,result)).filter(Boolean)
      .sort((a,b)=>Number(b.item.own)-Number(a.item.own)||a.extra-b.extra||(a.item.priceCents*a.qty)-(b.item.priceCents*b.qty)).slice(0,type==='accessory'?3:4);
  }

  // Make pure functions reachable for local regression tests (not needed by Shopify).
  if(typeof module!=='undefined'&&module.exports){module.exports={compute,classifyVerified,rankVerified,autonomyDays,batteryStoredWh,formatDays,DEFAULT_DEVICES,SUN_PRESETS};}
  if(typeof document==='undefined')return;

  const el=(tag,cls,content)=>{
    const node=document.createElement(tag);
    if(cls)node.className=cls;
    if(content!==undefined&&content!==null)node.textContent=String(content);
    return node;
  };
  const safeUrl=(raw,allowExternal=false)=>{
    try{
      const u=new URL(raw,window.location.origin);
      if(u.protocol!=='https:' && u.protocol!=='http:')return '';
      if(!allowExternal&&u.origin!==window.location.origin)return '';
      return u.href;
    }catch(e){return '';}
  };
  function imageFor(url,alt){
    const wrap=el('div','ptsc__product-img');
    const good=safeUrl(url,true);
    if(good){const img=el('img');img.src=good;img.alt=alt||'';img.loading='lazy';wrap.append(img);}
    else wrap.append(el('span','ptsc__product-img-fallback','Bild nicht vorhanden'));
    return wrap;
  }
  function currencyFmt(currency,cents){
    try{return new Intl.NumberFormat('de-DE',{style:'currency',currency:currency||'EUR'}).format((Number(cents)||0)/100);}
    catch(e){return fmt((Number(cents)||0)/100,2)+' €';}
  }
  function centsFromPredictive(value){const n=numeric(value,0);return Math.round(n*100);}

  function init(root){
    if(!root||root.dataset.ptscReady==='true')return;
    root.dataset.ptscReady='true';
    const form=root.querySelector('[data-ptsc-form]');
    if(!form)return;
    const panels=[...root.querySelectorAll('[data-ptsc-step]')];
    const tabs=[...root.querySelectorAll('[data-ptsc-tab]')];
    const prev=root.querySelector('[data-ptsc-prev]');
    const next=root.querySelector('[data-ptsc-next]');
    const counter=root.querySelector('[data-ptsc-counter]');
    const progress=root.querySelector('[data-ptsc-progress]');
    const error=root.querySelector('[data-ptsc-error]');
    const devContainer=root.querySelector('[data-ptsc-devices]');
    const currency=root.dataset.currency||'EUR';
    const ownVendor=(root.dataset.ownVendor||'Pantheratec').trim().toLowerCase();
    const catalogRoot=root.dataset.rootUrl||'/';
    const formVal=name=>form.elements.namedItem(name)?.value;
    let devices=copyDefaultDevices(),step=0,customIndex=0,currentResult=null;
    let verified=[];
    try{verified=JSON.parse(root.querySelector('[data-ptsc-products-json]')?.textContent||'[]');}
    catch(e){verified=[];}
    const suggestionCache=new Map();

    function fieldLabel(label){const span=el('span','ptsc__mobile-label',label);return span;}
    function deviceRow(device){
      const row=el('div','ptsc__device'+(!device.enabled?' is-off':''));row.dataset.deviceId=device.id;
      const name=el('div','ptsc__device-name');
      const toggle=el('input');toggle.type='checkbox';toggle.checked=!!device.enabled;toggle.dataset.field='enabled';toggle.setAttribute('aria-label',device.name+' berücksichtigen');name.append(toggle);
      if(device.custom){const label=el('input');label.type='text';label.value=device.name;label.maxLength=70;label.placeholder='Gerätename';label.dataset.field='name';label.setAttribute('aria-label','Name des eigenen Geräts');name.append(label);}
      else name.append(el('span','',device.name));
      row.append(name);
      for(const field of [{key:'watts',label:'Leistung (W)',min:1,max:5000,step:1},{key:'hours',label:'Stunden/Tag',min:.05,max:24,step:.05},{key:'duty',label:'Einschaltzeit (%)',min:1,max:100,step:1}]){
        const label=el('label','ptsc__device-number');label.append(fieldLabel(field.label));
        const inp=el('input');inp.type='number';inp.value=String(device[field.key]);inp.min=field.min;inp.max=field.max;inp.step=field.step;inp.inputMode='decimal';inp.dataset.field=field.key;inp.setAttribute('aria-label',`${device.name}: ${field.label}`);label.append(inp);row.append(label);
      }
      const total=el('div','ptsc__device-total');const wh=el('strong');wh.dataset.deviceWh='';total.append(wh);
      if(device.custom){const remove=el('button','ptsc__remove','×');remove.type='button';remove.dataset.remove='';remove.title='Gerät entfernen';remove.setAttribute('aria-label',device.name+' entfernen');total.append(remove);}
      row.append(total);
      const extra=el('div','ptsc__device-extra');const qtyLabel=el('label');qtyLabel.append(el('span','', 'Anzahl'));
      const qty=el('input');qty.type='number';qty.min='1';qty.max='20';qty.step='1';qty.value=String(device.qty);qty.dataset.field='qty';qty.setAttribute('aria-label',device.name+' Anzahl');qtyLabel.append(qty);extra.append(qtyLabel);
      const typeLabel=el('label');typeLabel.append(el('span','', 'Versorgung'));
      const source=el('select');source.dataset.field='source';source.setAttribute('aria-label',device.name+' Stromart');
      for(const [value,label] of [['dc','12/24 V (DC)'],['ac','230 V (Wechselrichter)']]){const option=el('option','',label);option.value=value;option.selected=device.source===value;source.append(option);}
      typeLabel.append(source);extra.append(typeLabel);row.append(extra);
      return row;
    }
    function updateDeviceTotals(){
      let rawTotal=0;
      for(const device of devices){
        const value=Number(device.enabled?clamp(numeric(device.watts,0),0,5000)*clamp(numeric(device.hours,0),0,24)*(clamp(numeric(device.duty,0),0,100)/100)*clamp(numeric(device.qty,1),1,20):0);
        rawTotal+=value;
        const row=devContainer.querySelector(`[data-device-id="${device.id}"]`);
        if(row){const out=row.querySelector('[data-device-wh]');if(out)out.textContent=fmt(Math.round(value))+' Wh';row.classList.toggle('is-off',!device.enabled);}
      }
      root.querySelector('[data-ptsc-live-total]').textContent=fmt(Math.round(rawTotal))+' Wh';
    }
    function renderDevices(){
      devContainer.querySelectorAll('[data-device-id]').forEach(x=>x.remove());
      devices.forEach(device=>devContainer.append(deviceRow(device)));
      updateDeviceTotals();
    }
    devContainer.addEventListener('input',event=>{
      const inp=event.target;const row=inp.closest('[data-device-id]');if(!row||!inp.dataset.field)return;
      const device=devices.find(x=>x.id===row.dataset.deviceId);if(!device)return;
      const f=inp.dataset.field;
      if(f==='name')device.name=inp.value;
      else if(f!=='enabled'&&f!=='source')device[f]=numeric(inp.value,NaN);
      updateDeviceTotals();
    });
    devContainer.addEventListener('change',event=>{
      const inp=event.target;const row=inp.closest('[data-device-id]');if(!row||!inp.dataset.field)return;
      const device=devices.find(x=>x.id===row.dataset.deviceId);if(!device)return;
      const f=inp.dataset.field;
      if(f==='enabled')device.enabled=inp.checked;
      else if(f==='source')device.source=inp.value;
      else if(f==='name')device.name=inp.value;
      else device[f]=numeric(inp.value,NaN);
      updateDeviceTotals();
    });
    devContainer.addEventListener('click',event=>{
      const btn=event.target.closest('[data-remove]');if(!btn)return;
      const id=btn.closest('[data-device-id]')?.dataset.deviceId;
      devices=devices.filter(d=>d.id!==id);renderDevices();
    });
    root.querySelector('[data-ptsc-add]').addEventListener('click',()=>{
      if(devices.length>=22){showError('Maximal 22 Geräte sind möglich. Bitte fasse weitere Geräte zusammen.');return;}
      hideError();customIndex+=1;
      devices.push({id:'custom-'+customIndex,name:'Eigenes Gerät',watts:35,hours:2,duty:100,qty:1,source:'dc',enabled:true,custom:true});
      renderDevices();devContainer.querySelector(`[data-device-id="custom-${customIndex}"] input[type="text"]`)?.focus();
    });
    function adjustSunPreset(){
      const region=formVal('region');const season=formVal('season');const hours=SUN_PRESETS[region]?.[season];
      if(hours!==undefined)form.elements.namedItem('sun_hours').value=String(hours);
    }
    root.querySelector('[data-ptsc-region]').addEventListener('change',adjustSunPreset);
    root.querySelector('[data-ptsc-season]').addEventListener('change',adjustSunPreset);
    function showError(msg){error.textContent=msg;error.hidden=false;error.scrollIntoView({block:'nearest',behavior:'smooth'});}
    function hideError(){error.hidden=true;error.textContent='';}
    function validate(current){
      if(current===0){
        const hours=numeric(formVal('sun_hours'));
        if(!isNumber(hours)||hours<.5||hours>8)return 'Bitte Sonnenstunden zwischen 0,5 und 8 angeben.';
        const roofL=formVal('roof_length'),roofW=formVal('roof_width');
        if((roofL&&!roofW)||(roofW&&!roofL))return 'Bitte bei der Dachfläche beide Maße ausfüllen oder beide leer lassen.';
        if((roofL&&(!isNumber(numeric(roofL))||numeric(roofL)<=0||numeric(roofL)>10000))||(roofW&&(!isNumber(numeric(roofW))||numeric(roofW)<=0||numeric(roofW)>3000)))return 'Bitte gültige Dachmaße in Millimetern angeben.';
      }
      if(current===1){
        const active=devices.filter(d=>d.enabled);
        if(active.length===0)return 'Bitte mindestens einen Stromverbraucher auswählen.';
        for(const d of active){
          if(!d.name?.trim())return 'Bitte einen Gerätenamen eintragen.';
          if(!isNumber(d.watts)||d.watts<=0||d.watts>5000||!isNumber(d.hours)||d.hours<=0||d.hours>24||!isNumber(d.duty)||d.duty<=0||d.duty>100||!isNumber(d.qty)||d.qty<1||d.qty>20||!Number.isInteger(d.qty))return `Bitte die Werte von „${d.name}“ prüfen (Watt, h/Tag, Einschaltzeit und Anzahl).`;
        }
      }
      if(current===2){
        const existing=numeric(formVal('existing_ah'));
        if(!isNumber(existing)||existing<0||existing>3000)return 'Bitte eine gültige vorhandene Batteriekapazität zwischen 0 und 3000 Ah angeben.';
        const charge=numeric(formVal('starting_soc'));
        if(!isNumber(charge)||charge<0||charge>100)return 'Bitte einen gültigen Batterieladestand zwischen 0 und 100 % angeben.';
      }
      return '';
    }
    function collect(){
      return {
        region:formVal('region'),season:formVal('season'),sunHours:numeric(formVal('sun_hours'),4),
        voltage:numeric(formVal('voltage'),12),shade:numeric(formVal('shade'),.85),
        reserve:numeric(formVal('reserve'),20)/100,
        roofLength:numeric(formVal('roof_length'),0),roofWidth:numeric(formVal('roof_width'),0),
        batteryType:formVal('battery_type'),autonomy:numeric(formVal('autonomy'),1),existingAh:numeric(formVal('existing_ah'),0),startingSoc:numeric(formVal('starting_soc'),100),
        inverterEfficiency:numeric(formVal('inverter_eff'),.9),devices:devices.map(x=>({...x}))
      };
    }
    function pill(content){return el('span','',content);}
    function clearAndAppend(container,nodes){container.replaceChildren(...nodes);}
    function appendInfo(container,kind,content){const box=el('div','ptsc__alert ptsc__alert--'+kind);box.append(el('b','',kind==='warn'?'!':'i'),el('span','',content));container.append(box);}
    function drawResults(result,input){
      currentResult=result;
      const vals={daily:fmt(Math.round(result.demandWh))+' Wh',solar:fmt(result.solarWp)+' Wp',battery:fmt(result.neededAh)+' Ah',mppt:fmt(result.minControllerA)+' A'};
      root.querySelectorAll('[data-ptsc-value]').forEach(n=>{n.textContent=vals[n.dataset.ptscValue]||'–';});
      clearAndAppend(root.querySelector('[data-ptsc-summary-pills]'),[
        pill(input.region==='individuell'?'Eigene Sonnenstunden':({mitteleuropa:'Mitteleuropa',suedeuropa:'Südeuropa',nordeuropa:'Nordeuropa'}[input.region]||'Region')),
        pill(fmt(result.sun,1)+' Sonnenstunden/Tag'),pill(result.voltage+' V Bordnetz'),
        pill(({lifepo4:'LiFePO4',agm:'AGM / Gel',wet:'Nass-/Säurebatterie'})[result.batteryType]),pill(fmt(result.autonomy)+' Tag(e) ohne Solar'),pill('Entladegrenze: '+fmt(result.minSoc*100)+' %')
      ]);
      const alerts=root.querySelector('[data-ptsc-alerts]');alerts.replaceChildren();
      if(result.peakSingleACWatts>0)appendInfo(alerts,'info',`230-V-Geräte: Mindestens ein Verbraucher benötigt bis zu ${fmt(result.peakSingleACWatts)} W Anschlussleistung. Wechselrichter-Dauerleistung und Anlaufspitzen unabhängig von der Energierechnung prüfen.`);
      if(input.season==='winter')appendInfo(alerts,'warn','Winter-Szenario: Auch mit dieser Wp-Leistung können Schlechtwetterphasen den Ertrag stark reduzieren. Zusätzliche Ladequellen und Energiereserven einplanen.');
      if(result.roofM2>0 && result.solarWp>result.roofWpApprox)appendInfo(alerts,'warn',`Dachflächen-Check: ${fmt(result.roofM2,2)} m² ergeben bei pauschalen 180 Wp/m² nur rund ${fmt(result.roofWpApprox)} Wp. Prüfe die tatsächlichen Modulabmessungen und verfügbare Fläche.`);
      if(input.shade<=.6)appendInfo(alerts,'warn','Häufige Verschattung kann den Ertrag einer Modulverschaltung stark beeinflussen. Der verwendete Minderungsfaktor ist nur eine Näherung.');
      if(result.solarWp>1600)appendInfo(alerts,'warn','Große Solaranlage: Ab dieser Größenordnung sind mehrere Regler, Dachstatik, Stromgrenzen und die Verschaltung besonders sorgfältig zu planen.');
      if(result.existingAh>0&&result.deficitAh>0)appendInfo(alerts,'warn','Vorhandene Batterie reicht bei voller Ladung für die gewünschte Autarkie ohne Sonne rechnerisch nicht aus. Vorgeschlagen werden mögliche neue Gesamtspeicher, kein ungeprüfter Parallel-Zubau.');
      if(result.existingAh>0 && result.startingSoc<=result.minSoc)appendInfo(alerts,'danger',`Deine Batterie steht bei ${fmt(result.startingSoc*100)} % – also an oder unter der eingeplanten ${fmt(result.minSoc*100)}-%-Entladegrenze. Bis zu einer Aufladung steht für die Autarkie-Prognose keine zusätzliche Energie zur Verfügung.`);
      if(result.batteryType==='wet')appendInfo(alerts,'info','Nass-/Säurebatterie: Der 50-%-Grenzwert ist eine konservative Annahme für geeignete Versorgungsbatterien. Herkömmliche Starterbatterien sind für wiederholte tiefe Zyklen nicht ausgelegt.');
      // Highlight the protected autonomy forecast before the technical table.
      const forecast=root.querySelector('[data-ptsc-forecast]');forecast.replaceChildren();
      const heading=el('div','ptsc__forecast-heading');heading.append(el('span','ptsc__index','AUTARKIE-PROGNOSE'),el('h4','', 'Wie lange kannst du autark stehen?'));
      heading.append(el('p','',result.existingAh>0
        ? `Mit deiner vorhandenen ${fmt(result.existingAh)}-Ah-Batterie ab ${fmt(result.startingSoc*100)} % Ladestand – nur bis zur ${fmt(result.minSoc*100)}-%-Restladung.`
        : `Du hast keine vorhandene Batterie angegeben. Diese Prognose bezieht sich auf den neu empfohlenen ${fmt(result.neededAh)}-Ah-Speicher bei 100 % Startladung – nur bis ${fmt(result.minSoc*100)} % Restladung.`));
      forecast.append(heading);
      const fgrid=el('div','ptsc__forecast-grid');
      const scenarios=[
        ['Ohne Solarertrag',formatDays(result.forecastNoSunDays),'Schlechtwetter / kein Nachladen'],
        ['Mit 30 % des geplanten Solarertrags',formatDays(result.forecastWeakSunDays),'Beispiel für deutlich weniger Sonne'],
        ['Mit geplantem Tagesertrag',formatDays(result.forecastNormalSunDays),'Rechnerische Tagesenergiebilanz']
      ];
      scenarios.forEach(([label,value,desc],i)=>{
        const item=el('div','ptsc__forecast-card'+(i===0?' ptsc__forecast-card--focus':''));
        item.append(el('span','',label),el('strong','',value),el('small','',desc));fgrid.append(item);
      });forecast.append(fgrid);
      const p=el('p','ptsc__forecast-note',`Berechnungsgrenze: ${fmt(result.minSoc*100)} % Restladung statt 0 %. Die Solarwerte sind Tagesdurchschnitte, keine Wettervorhersage. „Tagesbilanz ausgeglichen“ bedeutet NICHT unbegrenzte Autarkie: Mehrere trübe Tage, Nachtbedarf, Temperatur und Alterung können die tatsächliche Zeit stark verkürzen. Herstellerwerte und Batteriemonitor haben Vorrang.`);
      forecast.append(p);
      if(result.existingAh>0 && result.neededAh>result.existingAh){
        const fullDays=autonomyDays(result.plannedBatteryWh,result.demandWh,0);
        forecast.append(el('p','ptsc__forecast-upgrade',`Mit einem voll geladenen neuen ${fmt(result.neededAh)}-Ah-Speicher wären rechnerisch etwa ${formatDays(fullDays)} ohne Solarertrag möglich – bis zur eingeplanten Restladung.`));
      }

      const battery=root.querySelector('[data-ptsc-battery-overview]');battery.replaceChildren();
      const batteryEntries=[['Vorhandene Kapazität',fmt(result.existingAh)+' Ah'],['Aktueller Ladezustand',fmt(result.startingSoc*100)+' %'],['Untergrenze / Entladestopp',fmt(result.minSoc*100)+' % Restladung'],['Aktuell nutzbare gespeicherte Energie',fmt(Math.round(result.currentBatteryWh))+' Wh'],['Ohne Solar ab jetzigem Ladestand',formatDays(result.daysAutonomy)],['Für dein Autarkieziel bei voller Ladung',fmt(result.neededAh)+' Ah'],['Rechnerische Kapazitätslücke (voll geladen)',fmt(result.deficitAh)+' Ah']];
      battery.append(makeList(batteryEntries));
      const formula=root.querySelector('[data-ptsc-formula]');formula.replaceChildren();
      formula.append(makeList([
        ['DC-Verbrauch',fmt(Math.round(result.dcWh))+' Wh'],['230-V-Verbrauch',fmt(Math.round(result.acWh))+' Wh'],
        ['Energiebedarf inklusive Verlusten',fmt(Math.round(result.demandWh))+' Wh'],
        ['System- / Verschattungsfaktor',fmt(result.systemFactor*100)+' % / '+fmt(result.shade*100)+' %'],
        ['Solare Tagesenergie (Beispiel)',fmt(Math.round(result.dayGenerationWh))+' Wh'],
        ['Speicher-Nutzenergie ab 100 %',fmt(Math.round(result.plannedBatteryWh))+' Wh'],
        ['Batterie-Entladetiefe',fmt(result.depth*100)+' % (bis '+fmt(result.minSoc*100)+' % SOC)'],
        ['Reserve auf Tagesbedarf',fmt(result.reserve*100)+' %']
      ]));
      const breakdown=root.querySelector('[data-ptsc-breakdown]');breakdown.replaceChildren();
      for(const d of result.devices){const row=el('div','ptsc__breakdown-row');row.append(el('span','',d.name+(d.source==='ac'?' · 230 V':' · DC')),el('strong','',fmt(Math.round(d.wh))+' Wh'));breakdown.append(row);}
      root.querySelector('[data-ptsc-breakdown-sub]').textContent=result.devices.length+' aktive Verbraucher';
      renderAllProducts(result,input);
      if(Array.isArray(window.dataLayer))window.dataLayer.push({event:'ptsc_calculator_completed',ptsc_solar_wp:result.solarWp,ptsc_daily_wh:Math.round(result.demandWh),ptsc_voltage:result.voltage});
    }
    function makeList(pairs){const ul=el('ul');for(const [label,value] of pairs){const li=el('li');li.append(el('span','',label),el('strong','',value));ul.append(li);}return ul;}
    function createCard(record,badgeText,isCandidate=false){
      const card=el('article','ptsc__product-card');card.append(imageFor(record.image,record.title));
      const content=el('div','ptsc__product-content');
      const badge=el('span','ptsc__product-badge '+(isCandidate?'ptsc__product-badge--candidate':(record.own?'ptsc__product-badge--own':'')),badgeText);content.append(badge);
      content.append(el('div','ptsc__product-title',record.title));
      if(record.sub)content.append(el('div','ptsc__product-spec',record.sub));
      if(record.autonomyLine && !isCandidate)content.append(el('div','ptsc__product-autonomy',record.autonomyLine));
      if(record.priceCents!==null && record.priceCents!==undefined)content.append(el('div','ptsc__product-price',currencyFmt(currency,record.priceCents)));
      const url=safeUrl(record.url);
      if(url){const a=el('a','ptsc__product-link',isCandidate?'Produktdaten prüfen →':'Zum Produkt →');a.href=url;a.dataset.ptscProductClick='';a.addEventListener('click',()=>{
        if(Array.isArray(window.dataLayer))window.dataLayer.push({event:'ptsc_product_click',ptsc_product_category:record.type||'',ptsc_product_origin:isCandidate?'shop_search':(record.own?'pantheratec':'verified_supplier')});
      });content.append(a);}
      card.append(content);return card;
    }
    function chooseQueries(type,result){
      if(type==='battery'&&result.batteryType==='agm')return ['AGM Batterie','Aufbaubatterie'];
      if(type==='battery'&&result.batteryType==='wet')return ['Nassbatterie','Blei Säure Versorgungsbatterie'];
      if(type==='solar'&&result.solarWp<=200)return ['100W Solarpanel','Solarmodul'];
      if(type==='solar'&&result.solarWp>600)return ['Solarmodul','Solar Komplettanlage'];
      return QUERY_TERMS[type]||[];
    }
    async function fetchCandidates(type,result){
      const terms=chooseQueries(type,result);
      let found=[];const used=new Set();
      for(const q of terms){
        try{
          const cacheKey=q;
          if(!suggestionCache.has(cacheKey)){
            const u=new URL('search/suggest.json',new URL(catalogRoot,window.location.origin));
            u.searchParams.set('q',q);u.searchParams.set('resources[type]','product');u.searchParams.set('resources[limit]','10');u.searchParams.set('resources[options][unavailable_products]','hide');
            const promise=fetch(u.toString(),{credentials:'same-origin',headers:{'Accept':'application/json'}}).then(r=>{if(!r.ok)throw Error('Shopify search '+r.status);return r.json();});
            suggestionCache.set(cacheKey,promise);
          }
          const json=await suggestionCache.get(cacheKey);
          for(const p of (json?.resources?.results?.products||[])){
            const productAvailable=p.available===true || (Array.isArray(p.variants) && p.variants.some(v=>v.available));
            const dedupKey=String(p.title||'').trim().toLowerCase();
            if(!productAvailable || used.has(p.handle||p.id) || used.has(dedupKey))continue;
            const text=(p.title+' '+(p.type||'')).toLowerCase();
            const match=type==='solar'?/(solar|photovolta|pv-modul)/i.test(text):type==='controller'?/(solar|laderegler|mppt)/i.test(text):type==='battery'?/(batterie|akku|lifepo|agm|nass|blei)/i.test(text):/(solar|kabel|halter|dach|stecker|verbinder)/i.test(text);
            if(!match)continue;
            used.add(p.handle||p.id);used.add(dedupKey);
            const vendor=String(p.vendor||'');
            const image=typeof p.image==='string'?p.image:(p.featured_image?.url||null);
            const price= (p.price_min!==undefined && p.price_min!==null)?p.price_min:p.price;
            found.push({type,own:vendor.toLowerCase()===ownVendor,title:p.title,vendor,image,url:p.url,priceCents:centsFromPredictive(price),sub:'Shop-Treffer · technische Eignung und Datenblatt überprüfen'});
          }
        }catch(e){/* handled by the display fallback; don't claim results exist */}
        if(found.length>=6)break;
      }
      return found.sort((a,b)=>Number(b.own)-Number(a.own)).slice(0,4);
    }
    function productSearchLink(type){
      const q=type==='solar'?'Solarmodul':type==='controller'?'MPPT Solarregler':type==='battery'?'Batterie Wohnmobil':'Solarkabel';
      const u=new URL('search',new URL(catalogRoot,window.location.origin));u.searchParams.set('q',q);u.searchParams.set('type','product');return u.href;
    }
    function createShopLink(type){const a=el('a','ptsc__product-link','Alle entsprechenden Produkte im Shop ansehen →');a.href=productSearchLink(type);return a;}
    function renderAllProducts(result,input){
      const types=['solar','controller','battery','accessory'];
      for(const type of types){
        const section=root.querySelector(`[data-ptsc-products="${type}"]`);if(!section)continue;
        section.replaceChildren();
        const head=el('div','ptsc__product-head');const title=el('h4','',RENDER_NAMES[type]);
        head.append(title);section.append(head);
        if(type==='battery' && result.neededAh<=result.existingAh){section.append(el('div','ptsc__product-none',result.daysAutonomy>=result.autonomy?'Deine vorhandene Batterie reicht mit dem aktuellen Ladestand rechnerisch für das Autarkieziel. Ein zusätzlicher Akku ist dafür nicht erforderlich.':'Deine Batterie ist bei voller Ladung ausreichend dimensioniert, aber mit dem aktuellen Ladestand noch nicht. Lade sie vor der Reise auf; ein weiterer Akku ist für die gewählte Zielzeit nicht grundsätzlich nötig.'));continue;}
        const ranked=rankVerified(verified,result,type);
        if(ranked.length){
          head.append(el('span','',ranked.length+' geprüfte Option'+(ranked.length===1?'':'en')));
          const grid=el('div','ptsc__products-grid');
          for(const x of ranked){
            const record={...x.item,sub:x.sub+(x.qty>1?' · Preis für '+x.qty+' Stück':'') ,priceCents:x.item.priceCents*x.qty};
            if(type==='battery'){
              const energy=batteryStoredWh(x.item.batteryAh,result,1);
              record.autonomyLine=`Mit dieser Batterie bei voller Ladung: voraussichtlich ${formatDays(autonomyDays(energy,result.demandWh,0))} ohne Solarertrag – bis ${fmt(result.minSoc*100)} % Restladung.`;
            } else if(type==='solar' && result.existingAh>0){
              const productSolarWh=x.totalWp*result.sun*result.systemFactor*result.shade;
              record.autonomyLine=`Mit deinen vorhandenen ${fmt(result.existingAh)} Ah und ${fmt(x.totalWp)} Wp: bei 30 % des berechneten Solarertrags voraussichtlich ${formatDays(autonomyDays(result.currentBatteryWh,result.demandWh,productSolarWh*.3))} – nur bis ${fmt(result.minSoc*100)} % Restladung.`;
            } else if(type==='solar'){
              record.autonomyLine='Für autarkes Stehen benötigst du zusätzlich eine passende Bordbatterie; ein Solarmodul allein speichert keinen Strom für die Nacht.';
            }
            grid.append(createCard(record,x.item.own?'Pantheratec · verifiziert':'Technische Daten hinterlegt · kaufbar'));
          }
          section.append(grid);
        }else{
          const msg=type==='solar'?'Noch kein verfügbares Solarmodul mit ausreichend hinterlegter Leistung und Stückzahl.':
            type==='controller'?'Noch kein verfügbarer Laderegler mit bestätigter Wp-/Ladestrom-Grenze für diese Auslegung.':
            type==='battery'?'Noch keine verfügbare Batterie, die als neuer Gesamtspeicher die benötigte Kapazität erfüllt.':
            'Noch keine geprüften, verfügbaren Solar-Zubehörprodukte hinterlegt.';
          section.append(el('div','ptsc__product-none',msg));
        }
        if(root.dataset.suggest==='true'){
          const label=el('div','ptsc__product-search-note','Weitere Shopartikel werden gesucht … · Suchtreffer sind keine technische Freigabe.');section.append(label);
          const requestResult=result;
          fetchCandidates(type,result).then(found=>{
            if(currentResult!==requestResult || !section.isConnected)return;
            if(found.length){
              label.textContent='Weitere lieferbare Shopartikel · Maße, Leistungsdaten und Kompatibilität selbst im Datenblatt prüfen:';
              const grid=el('div','ptsc__products-grid');grid.style.marginTop='10px';
              for(const p of found)grid.append(createCard(p,p.own?'Pantheratec im Shop · ungeprüft':'Shop-Treffer · ungeprüft',true));
              section.append(grid);
            }else label.textContent='Keine weiteren verfügbaren Shop-Treffer gefunden. Du kannst die gesamte Produktgruppe durchsuchen.';
            label.append(el('span','', ' '),createShopLink(type));
          });
        }else section.append(createShopLink(type));
      }
    }
    function setStep(index,scroll=true){
      step=clamp(index,0,3);hideError();
      panels.forEach((panel,i)=>{panel.hidden=i!==step;});
      tabs.forEach((tab,i)=>{tab.classList.toggle('is-current',i===step);tab.classList.toggle('is-done',i<step);});
      counter.textContent='Schritt '+(step+1)+' von 4';progress.setAttribute('aria-valuenow',String(step+1));progress.querySelector('span').style.width=((step+1)/4*100)+'%';
      prev.hidden=step===0;next.hidden=step===3;
      next.textContent=step===0?'Weiter zu den Verbrauchern →':step===1?'Weiter zur Batterie →':'Solarbedarf berechnen →';
      if(scroll){const rect=root.querySelector('.ptsc__wizard').getBoundingClientRect();const offset=90;
        if(rect.top<0||rect.top>window.innerHeight*.6)window.scrollTo({top:window.scrollY+rect.top-offset,behavior:'smooth'});
      }
    }
    next.addEventListener('click',()=>{
      const errorMsg=validate(step);if(errorMsg){showError(errorMsg);return;}
      if(step===2){const data=collect();const out=compute(data);drawResults(out,data);setStep(3);}
      else setStep(step+1);
    });
    prev.addEventListener('click',()=>setStep(step-1));
    root.querySelector('[data-ptsc-restart]').addEventListener('click',()=>{
      form.reset();devices=copyDefaultDevices();renderDevices();adjustSunPreset();currentResult=null;setStep(0);try{window.history.replaceState(null,'',window.location.pathname+window.location.search);}catch(e){}
    });
    function encodeData(obj){
      const bytes=new TextEncoder().encode(JSON.stringify(obj));let binary='';for(const b of bytes)binary+=String.fromCharCode(b);
      return btoa(binary).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'');
    }
    function decodeData(str){
      const s=str.replace(/-/g,'+').replace(/_/g,'/');const bin=atob(s.padEnd(Math.ceil(s.length/4)*4,'='));const bytes=Uint8Array.from(bin,c=>c.charCodeAt(0));return JSON.parse(new TextDecoder().decode(bytes));
    }
    root.querySelector('[data-ptsc-copy]').addEventListener('click',async()=>{
      if(!currentResult)return;
      const u=new URL(window.location.href);u.hash='solar='+encodeData(collect());
      const feedback=root.querySelector('[data-ptsc-copy-feedback]');
      try{await navigator.clipboard.writeText(u.href);feedback.textContent='Der Link mit deinen Einstellungen wurde kopiert.';}
      catch(e){feedback.textContent='Kopieren ist im Browser gesperrt. Bitte diese Adresse manuell kopieren: '+u.href;}
    });
    root.querySelector('[data-ptsc-print]').addEventListener('click',()=>window.print());
    function restoreShared(){
      const hash=window.location.hash;
      if(!hash.startsWith('#solar='))return;
      try{
        const shared=decodeData(hash.slice(7));
        if(!shared||typeof shared!=='object'||!Array.isArray(shared.devices)||shared.devices.length>22)return;
        const vals={region:shared.region,season:shared.season,sun_hours:shared.sunHours,voltage:shared.voltage,shade:shared.shade,
          reserve:Math.round(numeric(shared.reserve,.2)*100),roof_length:shared.roofLength||'',roof_width:shared.roofWidth||'',
          battery_type:shared.batteryType,autonomy:shared.autonomy,existing_ah:shared.existingAh,starting_soc:shared.startingSoc===undefined?100:shared.startingSoc,inverter_eff:shared.inverterEfficiency};
        for(const [name,value] of Object.entries(vals)){
          const field=form.elements.namedItem(name);if(field && value!==undefined && value!==null){field.value=String(value);}
        }
        devices=shared.devices.map((d,i)=>({
          id:'restored-'+i,name:String(d.name||'Gerät').slice(0,70),watts:clamp(numeric(d.watts,10),1,5000),
          hours:clamp(numeric(d.hours,1),.05,24),duty:clamp(numeric(d.duty,100),1,100),
          qty:clamp(Math.round(numeric(d.qty,1)),1,20),source:d.source==='ac'?'ac':'dc',enabled:!!d.enabled,custom:!!d.custom
        }));
        if(validate(0)||validate(1)||validate(2))return;
        renderDevices();const data=collect();drawResults(compute(data),data);setStep(3,false);
      }catch(e){/* invalid share URL: preserve empty form */}
    }
    renderDevices();adjustSunPreset();setStep(0,false);restoreShared();
  }
  function initAll(context){
    if(context?.matches?.('[data-ptsc]'))init(context);
    (context||document).querySelectorAll?.('[data-ptsc]').forEach(init);
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>initAll(document));
  else initAll(document);
  document.addEventListener('shopify:section:load',event=>initAll(event.target));
})();
